import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './Navbar';
import { events } from '../utils/analytics';
import { PageSEO } from '../utils/seo';

/* ─── Mini onde animate per header card ─── */
function CardWaves({ color, active }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const tRef      = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const lines = [
      { amp: 18, freq: 0.012, phase: 0,    speed: 0.018, width: 1.5, alpha: 0.55 },
      { amp: 12, freq: 0.018, phase: 2.1,  speed: 0.024, width: 1.0, alpha: 0.40 },
      { amp: 22, freq: 0.009, phase: 4.3,  speed: 0.014, width: 2.0, alpha: 0.65 },
      { amp: 9,  freq: 0.022, phase: 1.4,  speed: 0.030, width: 0.8, alpha: 0.30 },
    ];

    const draw = () => {
      const W = canvas.width;
      const H = canvas.height;
      const t = tRef.current;
      const boost = active ? 1.75 : 1;

      ctx.clearRect(0, 0, W, H);

      lines.forEach(line => {
        const path = new Path2D();
        for (let x = 0; x <= W; x += 2) {
          const y = H / 2 + line.amp * boost * Math.sin(x * line.freq + t * line.speed + line.phase);
          x === 0 ? path.moveTo(x, y) : path.lineTo(x, y);
        }

        /* outer bloom */
        ctx.strokeStyle = color;
        ctx.lineWidth   = line.width * boost * 5;
        ctx.globalAlpha = line.alpha * boost * 0.08;
        ctx.stroke(path);

        /* mid glow */
        ctx.lineWidth   = line.width * boost * 2;
        ctx.globalAlpha = line.alpha * boost * 0.25;
        ctx.stroke(path);

        /* core */
        ctx.lineWidth   = line.width * boost;
        ctx.globalAlpha = line.alpha * boost * 0.9;
        ctx.stroke(path);

        ctx.globalAlpha = 1;
      });

      tRef.current += 1;
      animRef.current = requestAnimationFrame(draw);
    };

    const resize = () => {
      canvas.width  = canvas.offsetWidth  * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    animRef.current = requestAnimationFrame(draw);

    return () => cancelAnimationFrame(animRef.current);
  }, [color, active]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        opacity: active ? 1 : 0.55,
        transition: 'opacity 0.4s',
        pointerEvents: 'none',
      }}
    />
  );
}

/* ─── Reveal on scroll ─── */
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Status config ─── */
const STATUS = {
  live:    { label: 'Live',      color: '#22d3ee', bg: 'rgba(34,211,238,0.1)',   border: 'rgba(34,211,238,0.28)'   },
  review:  { label: 'In Review', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',   border: 'rgba(245,158,11,0.28)'   },
  beta:    { label: 'Beta',      color: '#a78bfa', bg: 'rgba(167,139,250,0.1)',  border: 'rgba(167,139,250,0.28)'  },
  concept: { label: 'Concept',   color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', border: 'rgba(148,163,184,0.22)'  },
};

const FILTERS = ['Tutti', 'Live', 'In Sviluppo', 'Concept'];

const PROGETTI = [
  {
    id: 'veyro',
    name: 'Veyro',
    emoji: '🏋️',
    tagline: "Un'app, ogni trainer — marketplace personal training",
    description: 'Schede di allenamento, piani alimentari e marketplace PT con split payment. In beta test su TestFlight e Play Internal.',
    stack: ['React Native', 'Expo', 'Supabase', 'Stripe Connect', 'Reanimated 4'],
    status: 'beta',
    filter: 'In Sviluppo',
    link: '/veyro',
    linkLabel: 'Scopri',
    internal: true,
  },
  {
    id: 'veyro-trainer',
    name: 'Veyro Trainer',
    emoji: '🧑‍🏫',
    tagline: 'App per personal trainer — builder schede e vendita coaching',
    description: 'Gestione clienti, piani alimentari, pacchetti in vendita e payout Stripe Connect. Zero costi fissi per il PT, in beta test su TestFlight e Play Internal.',
    stack: ['React Native', 'Expo', 'Supabase', 'Stripe Connect', 'Reanimated 4'],
    status: 'beta',
    filter: 'In Sviluppo',
    link: '/veyro-trainer',
    linkLabel: 'Scopri',
    internal: true,
  },
  {
    id: 'ipairly',
    name: 'iPairly',
    emoji: '💑',
    tagline: 'App per coppie — giochi interattivi offline-first',
    description: '223 actions, IAP, LDR mode con Realtime. Dati crittografati on-device, zero backend, zero ads.',
    stack: ['React Native', 'Expo', 'Supabase', 'SQLite', 'Zustand'],
    status: 'review',
    filter: 'Live',
    link: '/ipairly',
    linkLabel: 'Scopri',
    internal: true,
  },
  {
    id: 'luxora',
    name: 'LUXORA',
    emoji: '🏛️',
    tagline: 'Digital Guardian per ville e yacht — Costiera Amalfitana',
    description: 'Piattaforma multi-app (sito, admin, client, worker), i18n 4 lingue, RLS Supabase, pricing dinamico.',
    stack: ['Next.js', 'React 19', 'Supabase', 'Turborepo', 'Resend'],
    status: 'live',
    filter: 'Live',
    link: 'https://grandiosoluxora.com',
    linkLabel: 'Visita',
    internal: false,
  },
  {
    id: 'accademiacampania',
    name: 'Accademia Campania',
    emoji: '🏆',
    tagline: 'Piattaforma Élite per la danza sportiva — stage, gare e formazione',
    description: 'Gerarchia Admin → Tecnico → Atleta: eventi, prenotazioni, diario atleta. RLS Supabase verificata con pgTAP, hero cinematografica GSAP.',
    stack: ['Next.js 16', 'React 19', 'Supabase', 'Turborepo', 'GSAP', 'Brevo'],
    status: 'live',
    filter: 'Live',
    link: '/progetti/accademia-campania',
    linkLabel: 'Case study',
    internal: true,
  },
  {
    id: 'icoach',
    name: 'iCoach',
    emoji: '💪',
    tagline: 'App fitness per personal trainer e clienti',
    description: 'Workout personalizzati, piani alimentari, check fisici con foto, video offline. Dual-screen smartphone + tablet.',
    stack: ['React Native', 'Expo', 'Firebase', 'Redux Toolkit'],
    status: 'live',
    filter: 'Live',
    link: '/icoach',
    linkLabel: 'Scopri',
    internal: true,
  },
  {
    id: 'sorrentoluxury',
    name: 'Sorrento Luxury',
    emoji: '🛥️',
    tagline: 'E-commerce noleggio barche e veicoli di lusso',
    description: 'WooCommerce personalizzato, booking multi-day, referral manager e pricing dinamico con extra services.',
    stack: ['WordPress', 'WooCommerce', 'PHP', 'MySQL'],
    status: 'live',
    filter: 'Live',
    link: 'https://www.sorrentoluxury.net',
    linkLabel: 'Visita',
    internal: false,
  },
  {
    id: 'shooke',
    name: 'Shooke',
    emoji: '💃',
    tagline: 'Piattaforma white-label di scoring per danza sportiva',
    description: 'Offline-first, audit log immutabile, setup <15 min da zero a gara live. Multi-organizzatore, multi-federazione.',
    stack: ['React Native', 'Expo', 'React', 'Vite', 'Supabase', 'MMKV'],
    status: 'beta',
    filter: 'In Sviluppo',
    link: null,
    linkLabel: null,
    internal: false,
  },
  {
    id: 'crewboat',
    name: 'CrewBoat',
    emoji: '⚓',
    tagline: 'Gestione equipaggi nautici',
    description: 'Ruoli captain/crew, comunicazioni, manutenzione, inventario. Offline-first con biometria e sync automatico.',
    stack: ['React Native', 'Expo', 'Firebase', 'Zustand'],
    status: 'beta',
    filter: 'In Sviluppo',
    link: '/crewboat',
    linkLabel: 'Scopri',
    internal: true,
  },
  {
    id: 'transfer',
    name: 'Transfer Multi-Modale',
    emoji: '🚗',
    tagline: 'Super-app transfer per Costiera Amalfitana e Napoli',
    description: "NCC + Boat Taxi + Taxi in un'unica app. Routing nautico proprietario, Stripe Connect, dynamic pricing.",
    stack: ['React Native', 'Node.js', 'Fastify', 'Supabase', 'Mapbox', 'Stripe'],
    status: 'concept',
    filter: 'Concept',
    link: null,
    linkLabel: null,
    internal: false,
  },
  {
    id: 'campaniacard',
    name: 'Campania Card',
    emoji: '🏖️',
    tagline: 'Piattaforma turistica integrata per la Campania',
    description: 'Concierge digitale con carta prepagata proprietaria, pianificazione intelligente e network partner.',
    stack: ['React Native', 'React.js', 'Node.js', 'Supabase', 'Stripe'],
    status: 'concept',
    filter: 'Concept',
    link: null,
    linkLabel: null,
    internal: false,
  },
];

/* ─── Project Card ─── */
function ProjectCard({ project, index }) {
  const [hov, setHov] = useState(false);
  const s = STATUS[project.status];

  const ctaProps = {
    style: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
      width: '100%',
      fontFamily: 'var(--font-mono)', fontSize: '0.88rem', fontWeight: 600,
      color: hov ? '#fff' : s.color,
      textDecoration: 'none',
      padding: '14px 20px',
      border: `1px solid ${hov ? s.color : s.border}`,
      borderRadius: '12px',
      background: hov ? `${s.color}28` : s.bg,
      transition: 'all 0.25s',
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 48 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24, scale: 0.96 }}
      transition={{ duration: 0.55, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: 'rgba(14,14,24,1)',
        border: `1px solid ${hov ? s.color + '70' : s.color + '30'}`,
        borderRadius: '24px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'border-color 0.3s, box-shadow 0.3s',
        boxShadow: hov
          ? `0 0 60px ${s.color}20, 0 16px 64px rgba(0,0,0,0.6)`
          : `0 0 0 0 transparent, 0 4px 32px rgba(0,0,0,0.4)`,
      }}
    >
      {/* ── Header con onde animate ── */}
      <div style={{
        position: 'relative',
        padding: '2.25rem 2.25rem 2rem',
        background: `linear-gradient(180deg, ${s.color}14 0%, ${s.color}06 100%)`,
        borderBottom: `1px solid ${s.color}${hov ? '40' : '20'}`,
        overflow: 'hidden',
        minHeight: 160,
        transition: 'border-color 0.3s',
      }}>
        {/* Canvas onde */}
        <CardWaves color={s.color} active={hov} />

        {/* Overlay scuro sotto il testo */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, transparent 30%, rgba(14,14,24,0.55) 100%)',
          pointerEvents: 'none',
        }} />

        {/* Indicatore selezione — linea sinistra + angolo highlight */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: hov ? '4px' : '2px',
          background: `linear-gradient(180deg, ${s.color}, ${s.color}44)`,
          boxShadow: hov ? `0 0 12px ${s.color}` : 'none',
          transition: 'width 0.25s, box-shadow 0.25s',
          borderRadius: '0 4px 4px 0',
        }} />
        {hov && (
          <div style={{
            position: 'absolute', top: 12, left: 12,
            fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
            color: s.color, opacity: 0.7, letterSpacing: '0.1em',
            textTransform: 'uppercase',
          }}>
            — selected
          </div>
        )}

        {/* Status badge + emoji */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.75rem', fontWeight: 600,
            color: s.color, background: `${s.color}18`,
            border: `1px solid ${s.color}50`,
            borderRadius: '8px', padding: '5px 12px',
            letterSpacing: '0.04em',
          }}>
            ● {s.label}
          </span>
          <div style={{
            width: 64, height: 64, borderRadius: '18px',
            background: `${s.color}18`,
            border: `1px solid ${s.color}45`,
            boxShadow: `0 0 24px ${s.color}30`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2rem',
            transition: 'transform 0.3s, box-shadow 0.3s',
            transform: hov ? 'scale(1.1) rotate(-4deg)' : 'scale(1)',
          }}>
            {project.emoji}
          </div>
        </div>

        {/* Name */}
        <h3 style={{
          fontFamily: 'var(--font-display)', fontWeight: 800,
          fontSize: 'clamp(1.5rem, 2.5vw, 1.9rem)',
          letterSpacing: '-0.03em', lineHeight: 1.1,
          color: 'var(--color-text)',
          margin: '0 0 0.6rem',
        }}>
          {project.name}
        </h3>

        {/* Tagline */}
        <p style={{
          fontFamily: 'var(--font-body)', fontSize: '0.95rem', fontWeight: 500,
          color: s.color, margin: 0, lineHeight: 1.4, opacity: 0.9,
        }}>
          {project.tagline}
        </p>
      </div>

      {/* ── Body ── */}
      <div style={{ padding: '1.75rem 2.25rem', display: 'flex', flexDirection: 'column', flex: 1, gap: '1.25rem' }}>

        {/* Description */}
        <p style={{
          fontSize: '1rem', color: 'rgba(240,240,248,0.72)',
          lineHeight: 1.75, margin: 0, flex: 1,
        }}>
          {project.description}
        </p>

        {/* Stack */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {project.stack.map(tech => (
            <span key={tech} className="stack-badge">{tech}</span>
          ))}
        </div>

        {/* CTA */}
        {project.link ? (
          project.internal ? (
            <Link to={project.link} {...ctaProps} onClick={() => events.projectClick(project.name)}>
              {project.linkLabel}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 7h10M7 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          ) : (
            <a href={project.link} target="_blank" rel="noreferrer" {...ctaProps} onClick={() => events.projectClick(project.name)}>
              {project.linkLabel}
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2.5 2.5h9v9M11.5 2.5L2.5 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          )
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '13px 20px', borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(255,255,255,0.02)',
            fontFamily: 'var(--font-mono)', fontSize: '0.82rem',
            color: 'var(--color-muted)',
          }}>
            Prossimamente
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ─── Page ─── */
export default function ProgettiPage() {
  const [activeFilter, setActiveFilter] = useState('Tutti');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = activeFilter === 'Tutti'
    ? PROGETTI
    : PROGETTI.filter(p => p.filter === activeFilter);

  const counts = {
    Tutti:         PROGETTI.length,
    Live:          PROGETTI.filter(p => p.filter === 'Live').length,
    'In Sviluppo': PROGETTI.filter(p => p.filter === 'In Sviluppo').length,
    Concept:       PROGETTI.filter(p => p.filter === 'Concept').length,
  };

  return (
    <>
      <PageSEO
        title="Portfolio progetti: app mobile, SaaS e web app"
        description="Scopri i prodotti digitali sviluppati da VMDProject: iCoach, CrewBoat, iPairly e altri. App mobile iOS e Android, SaaS B2B e web app."
        canonical="/progetti"
      />
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: 'var(--color-bg)', minHeight: '100vh' }}
      >

        {/* ── Hero ── */}
        <section style={{
          paddingTop: '68px',
          background: 'linear-gradient(180deg, rgba(124,58,237,0.07) 0%, transparent 100%)',
          borderBottom: '1px solid var(--color-border)',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute', top: '-80px', right: '-80px',
            width: 400, height: 400, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: '-60px', left: '10%',
            width: 300, height: 300, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(34,211,238,0.06) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          <div className="container" style={{ padding: '5rem 1.75rem 4rem', position: 'relative' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.82rem',
                color: 'var(--color-accent-mid)', letterSpacing: '0.12em',
                textTransform: 'uppercase', display: 'block', marginBottom: '1.25rem',
              }}>{'// portfolio'}</span>

              <h1 style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(2.8rem, 6vw, 4.5rem)',
                letterSpacing: '-0.04em', lineHeight: 1.05,
                marginBottom: '1.25rem', maxWidth: '700px',
              }}>
                Progetti{' '}
                <span style={{
                  background: 'var(--gradient-text)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>costruiti</span>
                {' '}per durare.
              </h1>

              <p style={{
                fontFamily: 'var(--font-body)', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)',
                color: 'var(--color-text-2)', maxWidth: '540px', lineHeight: 1.7,
                marginBottom: '2.5rem',
              }}>
                Da app mobile a piattaforme SaaS complesse — ogni progetto nasce da un problema
                reale e viene costruito per scalare.
              </p>

              {/* Stats */}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {[
                  { label: 'Totale',         value: counts.Tutti, color: '#7c3aed' },
                  { label: 'In produzione',  value: counts.Live, color: '#22d3ee' },
                  { label: 'In sviluppo',    value: counts['In Sviluppo'], color: '#a78bfa' },
                  { label: 'In elaborazione',value: counts.Concept, color: '#94a3b8' },
                ].map(stat => (
                  <div key={stat.label} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '10px', padding: '8px 14px',
                  }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1.05rem', color: stat.color }}>
                      {stat.value}
                    </span>
                    <span style={{ fontSize: '0.82rem', color: 'var(--color-muted)' }}>{stat.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── Filters + Grid ── */}
        <div className="container" style={{ padding: '3.5rem 1.75rem 6rem' }}>

          {/* Filter tabs */}
          <Reveal>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '3rem' }}>
              {FILTERS.map(f => {
                const active = f === activeFilter;
                return (
                  <button
                    key={f}
                    onClick={() => setActiveFilter(f)}
                    style={{
                      fontFamily: 'var(--font-mono)', fontSize: '0.82rem', fontWeight: 500,
                      padding: '8px 18px', borderRadius: '10px',
                      border: active ? '1px solid rgba(124,58,237,0.5)' : '1px solid rgba(255,255,255,0.08)',
                      background: active ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.03)',
                      color: active ? 'var(--color-accent-mid)' : 'var(--color-muted)',
                      cursor: 'pointer', transition: 'all 0.2s',
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                  >
                    {f}
                    <span style={{
                      background: active ? 'rgba(124,58,237,0.3)' : 'rgba(255,255,255,0.06)',
                      borderRadius: '5px', padding: '1px 6px', fontSize: '0.72rem',
                      color: active ? 'var(--color-accent-lt)' : 'var(--color-muted)',
                    }}>
                      {counts[f]}
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>

          {/* Cards grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(460px, 100%), 1fr))',
                gap: '2rem',
              }}
            >
              {filtered.map((project, i) => (
                <ProjectCard key={project.id} project={project} index={i} />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ── CTA ── */}
        <Reveal>
          <section style={{
            borderTop: '1px solid var(--color-border)',
            padding: '5rem 0',
            background: 'linear-gradient(180deg, transparent 0%, rgba(124,58,237,0.04) 100%)',
          }}>
            <div className="container" style={{ textAlign: 'center' }}>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: '0.82rem',
                color: 'var(--color-accent-mid)', letterSpacing: '0.12em',
                textTransform: 'uppercase', display: 'block', marginBottom: '1rem',
              }}>{'// hai un progetto?'}</span>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                letterSpacing: '-0.03em', marginBottom: '1rem',
              }}>
                Costruiamo qualcosa{' '}
                <span style={{
                  background: 'var(--gradient-text)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                }}>insieme.</span>
              </h2>
              <p style={{
                fontSize: '1.05rem', color: 'var(--color-text-2)',
                maxWidth: '460px', margin: '0 auto 2.5rem', lineHeight: 1.7,
              }}>
                Dall'idea al prodotto finito. Raccontami cosa vuoi costruire.
              </p>
              <Link to="/collabora" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }} onClick={events.ctaCollabora}>
                Iniziamo a parlare
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </section>
        </Reveal>

        {/* ─── Footer ─── */}
        <footer style={{ borderTop: '1px solid var(--color-border)', padding: '2rem 0' }}>
          <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95rem' }}>VMDProject</span>
            <div style={{ display: 'flex', gap: '1.5rem' }}>
              {[{ to: '/policy', label: 'Privacy' }, { to: '/terms-and-conditions', label: 'Termini' }].map(l => (
                <Link key={l.to} to={l.to}
                  style={{ fontSize: '0.9rem', color: 'var(--color-muted)', transition: 'color 0.3s', textDecoration: 'none' }}
                  onMouseEnter={e => e.target.style.color = 'var(--color-text)'}
                  onMouseLeave={e => e.target.style.color = 'var(--color-muted)'}
                >{l.label}</Link>
              ))}
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'var(--color-muted)' }}>
              © {new Date().getFullYear()} VMDProject · P.IVA 10990261215
            </span>
          </div>
        </footer>

      </motion.main>
    </>
  );
}
